import { useApolloClient, useMutation } from "@apollo/client";
import { useMediaLoadingDispatch } from "../../../../contexts/mediaLoading.context";
import { useNotificationDispatch } from "../../../../contexts/notification.context";
import { useUserState } from "../../../../contexts/user.context";
import { useCreativeState } from "../../../../contexts/creative.context";
import { delete_creative } from "../../../../helpers/delete_creative";
import { duplicate_creative } from "../../../../helpers/duplicate_creative";
import { GET_EXISTING_CREATIVE } from "../../../../graphql/queries";
import { logger } from "../../../../libs/logger";
import * as mutations from "../../../../graphql/mutations";
import { CreativeActionsProps } from "../types";

// Utility functions for creative actions
export function useCreativeActions({ deleteCreativeModal, setDeleteCreativeModal, refreshCreatives }: CreativeActionsProps) {
  const user = useUserState();
  const creative = useCreativeState();
  const notificationDispatch = useNotificationDispatch();
  const mediaLoadingDispatch = useMediaLoadingDispatch();
  const apolloClient = useApolloClient();

  const [saveNewCreative] = useMutation(mutations.SAVE_NEW_CREATIVE);
  const [deleteCreative] = useMutation(mutations.DELETE_CREATIVE);

  // Queries the client directly rather than via useLazyQuery: on @apollo/client 3.3 the lazy
  // execute function returns void, so awaiting it resolves to undefined instead of a result. KT
  const getOneCreativeData = async ({ variables }: { variables: { id: string; teamId: string } }) => {
    try {
      return await apolloClient.query({ query: GET_EXISTING_CREATIVE, variables, fetchPolicy: "network-only" });
    } catch (error) {
      logger({ error, user, context: { origin: "CreativeActions_GET_EXISTING_CREATIVE" } });
      throw error;
    }
  };

  const notifyCreativeActionFailed = () => {
    notificationDispatch({
      type: "ADD",
      payload: {
        header: "Error",
        message: "Something went wrong.",
        type: "error",
        autoDismiss: false,
      },
    });
  };

  const handleDuplicateCreative = async (e: any, existingCreativeId: string) => {
    e.preventDefault();
    try {
      const { data } = await getOneCreativeData({ variables: { id: existingCreativeId, teamId: user.teamId } });
      await duplicate_creative(data, data.readOneCreativeId.id, user, mediaLoadingDispatch, notificationDispatch, saveNewCreative);
      await refreshCreatives();
    } catch (error) {
      notifyCreativeActionFailed();
    }
  };

  const handleDeleteCreative = async (creativeId: any) => {
    try {
      const { data } = await getOneCreativeData({ variables: { id: creativeId, teamId: user.teamId } });
      await delete_creative({ id: data.readOneCreativeId.id, teamId: user.teamId }, deleteCreative, mediaLoadingDispatch, notificationDispatch);
      await refreshCreatives();
    } catch (error) {
      notifyCreativeActionFailed();
    }
  };

  const openDeleteCreativeModal = async (e: any, creativeId: string) => {
    e.preventDefault();
    setDeleteCreativeModal({ creative: creativeId, open: !deleteCreativeModal.open });
  };

  return { handleDuplicateCreative, openDeleteCreativeModal, handleDeleteCreative, saveNewCreative, deleteCreative, getOneCreativeData };
}
